import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, formatINR, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const CartDrawer = () => {
  const { items, isOpen, closeCart, updateQty, removeItem, clearCart, subtotal, count } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState("online");
  const [placedOrder, setPlacedOrder] = useState(null); // {order, paid, cod}
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [shipCfg, setShipCfg] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const discount = appliedCoupon?.discount || 0;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const shippingCost = shipCfg
    ? discountedSubtotal >= (shipCfg.free_shipping_above ?? Infinity)
      ? 0
      : Number(shipCfg.shipping_flat_rate || 0)
    : 0;
  const orderTotal = discountedSubtotal + shippingCost;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), subtotal });
      setAppliedCoupon({ code: data.code, discount: data.discount });
      toast.success(`Coupon ${data.code} applied — you save ${formatINR(data.discount)}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(apiError(e, "Invalid coupon code"));
    } finally {
      setCouponBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startCheckout = () => {
    closeCart();
    setPlacedOrder(null);
    setCheckoutOpen(true);
    api
      .get("/settings/checkout")
      .then((r) => setShipCfg(r.data))
      .catch(() => setShipCfg(null));
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email and phone number");
      return;
    }
    setPaying(true);
    try {
      const { data: order } = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        coupon_code: appliedCoupon?.code || undefined,
        payment_method: payMethod,
      });
      window.localStorage.setItem("aarmira_last_order_ref", order.id.slice(0, 8).toUpperCase());
      const { data: cfg } = await api.get("/payments/config");
      if (!cfg.configured) {
        clearCart();
        setAppliedCoupon(null);
        setCouponInput("");
        setPlacedOrder({ order, paid: false, cod: payMethod === "cod" });
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay");
      const { data: rpOrder } = await api.post("/payments/razorpay/order", { order_id: order.id });
      const rzp = new window.Razorpay({
        key: cfg.key_id,
        amount: rpOrder.amount,
        currency: "INR",
        name: "Aarmira",
        description: "Aarmira Jewellery",
        order_id: rpOrder.id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", { order_id: order.id, ...resp });
            clearCart();
            setAppliedCoupon(null);
            setCouponInput("");
            setPlacedOrder({ order, paid: true, cod: payMethod === "cod" });
          } catch {
            toast.error("Payment verification failed. If amount was deducted, it will be auto-refunded.");
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(apiError(err, "Could not place your order. Please try again."));
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              data-testid="cart-drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeCart}
              className="fixed inset-0 z-[60] bg-espresso/50 backdrop-blur-sm"
            />
            <motion.aside
              data-testid="cart-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-md flex-col border-l border-gold/30 bg-cream"
            >
              <div className="flex items-center justify-between border-b border-gold/20 px-6 py-5">
                <div>
                  <h2 className="font-serif text-2xl text-espresso">Your Cart</h2>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted">
                    {count} {count === 1 ? "treasure" : "treasures"}
                  </p>
                </div>
                <button
                  data-testid="cart-drawer-close-button"
                  onClick={closeCart}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-espresso/10 text-espresso transition-colors hover:border-gold"
                  aria-label="Close cart"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {items.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <ShoppingBag size={36} strokeWidth={1} className="text-gold-muted/50" />
                    <p className="mt-4 font-serif text-xl text-espresso">Your cart is empty</p>
                    <p className="mt-2 text-sm text-espresso/50">
                      Add a piece of fortune to begin.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-espresso/8">
                    {items.map(({ product, quantity }) => (
                      <li key={product.id} data-testid={`cart-item-${product.id}`} className="flex gap-4 py-4">
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-sm bg-cream-dark">
                          {product.images?.[0] && (
                            <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-serif text-base leading-snug text-espresso">{product.name}</p>
                            <button
                              data-testid={`cart-item-remove-button-${product.id}`}
                              onClick={() => removeItem(product.id)}
                              className="text-espresso/40 transition-colors hover:text-red-700"
                              aria-label="Remove item"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <p className="price-text mt-1 text-sm text-espresso">{formatINR(product.price)}</p>
                          <div className="mt-auto flex items-center gap-3 pt-2">
                            <button
                              data-testid={`cart-item-decrease-${product.id}`}
                              onClick={() => updateQty(product.id, quantity - 1)}
                              className="flex h-7 w-7 items-center justify-center border border-espresso/15 text-espresso transition-colors hover:border-gold"
                              aria-label="Decrease quantity"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="font-mono text-xs">{quantity}</span>
                            <button
                              data-testid={`cart-item-increase-${product.id}`}
                              onClick={() => updateQty(product.id, quantity + 1)}
                              className="flex h-7 w-7 items-center justify-center border border-espresso/15 text-espresso transition-colors hover:border-gold"
                              aria-label="Increase quantity"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {items.length > 0 && (
                <div className="border-t border-gold/20 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted">
                      Subtotal
                    </span>
                    <span data-testid="cart-subtotal" className="price-text text-2xl text-espresso">
                      {formatINR(subtotal)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-espresso/50">
                    Shipping &amp; taxes calculated at checkout.
                  </p>
                  <button
                    data-testid="cart-razorpay-checkout-button"
                    onClick={startCheckout}
                    className="mt-4 w-full bg-gold py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98]"
                  >
                    Checkout with Razorpay
                  </button>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="border-gold/30 bg-cream sm:max-w-md" data-testid="checkout-dialog">
          {placedOrder ? (
            <div className="py-6 text-center">
              <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-gold-accent" />
              <DialogTitle className="mt-4 font-serif text-2xl text-espresso">
                {placedOrder.paid
                  ? placedOrder.cod
                    ? "COD order confirmed"
                    : "Payment received"
                  : "Order placed"}
              </DialogTitle>
              <DialogDescription className="mt-3 text-sm leading-relaxed text-espresso/60">
                {placedOrder.paid
                  ? placedOrder.cod
                    ? `Your ₹200 shipping advance has been received. Keep ${formatINR(
                        placedOrder.order.cod_balance_due ?? 0
                      )} ready — payable on delivery. A confirmation email is on its way.`
                    : "Thank you. Your treasures are being prepared with care — a confirmation email is on its way."
                  : placedOrder.cod
                    ? "Your COD order is saved. A ₹200 non-refundable shipping advance is required to confirm it — our team will reach out shortly."
                    : "Online payments go live soon — your order is saved and a confirmation email is on its way; our team will reach out to complete your purchase."}
              </DialogDescription>
              <p
                data-testid={placedOrder.paid ? "checkout-success" : "checkout-payment-notice"}
                className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted"
              >
                Order #{placedOrder.order.id.slice(0, 8).toUpperCase()}
              </p>
              <button
                data-testid="checkout-done-button"
                onClick={() => setCheckoutOpen(false)}
                className="mt-6 w-full border border-espresso/20 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-espresso transition-colors hover:border-gold hover:bg-gold/10"
              >
                Continue Browsing
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-espresso">Checkout</DialogTitle>
                <DialogDescription asChild className="text-sm text-espresso/60">
                  <span className="block space-y-1.5 pt-1">
                    <span className="flex justify-between">
                      <span>Subtotal</span>
                      <span className="price-text text-espresso">{formatINR(subtotal)}</span>
                    </span>
                    {discount > 0 && (
                      <span className="flex justify-between">
                        <span>Coupon {appliedCoupon.code}</span>
                        <span data-testid="checkout-discount" className="text-gold-muted">
                          −{formatINR(discount)}
                        </span>
                      </span>
                    )}
                    <span className="flex justify-between">
                      <span>Shipping</span>
                      <span
                        data-testid="checkout-shipping"
                        className={shippingCost === 0 ? "text-gold-muted" : "text-espresso"}
                      >
                        {shipCfg === null ? "…" : shippingCost === 0 ? "FREE" : formatINR(shippingCost)}
                      </span>
                    </span>
                    <span className="flex justify-between border-t border-espresso/10 pt-1.5">
                      <span>Total</span>
                      <span data-testid="checkout-total" className="price-text text-espresso">
                        {formatINR(orderTotal)}
                      </span>
                    </span>
                  </span>
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handlePay} className="mt-2 space-y-3">
                <input
                  data-testid="checkout-name-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Full name"
                  className="w-full border border-espresso/15 bg-transparent px-4 py-3 text-sm text-espresso outline-none transition-colors placeholder:text-espresso/35 focus:border-gold"
                />
                <input
                  data-testid="checkout-email-input"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email"
                  className="w-full border border-espresso/15 bg-transparent px-4 py-3 text-sm text-espresso outline-none transition-colors placeholder:text-espresso/35 focus:border-gold"
                />
                <input
                  data-testid="checkout-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone (10-digit mobile)"
                  className="w-full border border-espresso/15 bg-transparent px-4 py-3 text-sm text-espresso outline-none transition-colors placeholder:text-espresso/35 focus:border-gold"
                />
                <textarea
                  data-testid="checkout-address-input"
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Delivery address (optional)"
                  rows={2}
                  className="w-full resize-none border border-espresso/15 bg-transparent px-4 py-3 text-sm text-espresso outline-none transition-colors placeholder:text-espresso/35 focus:border-gold"
                />
                {appliedCoupon ? (
                  <div className="flex items-center justify-between border border-gold/40 bg-gold/10 px-4 py-2.5">
                    <span
                      data-testid="checkout-coupon-applied"
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-muted"
                    >
                      {appliedCoupon.code} applied — you save {formatINR(appliedCoupon.discount)}
                    </span>
                    <button
                      type="button"
                      data-testid="checkout-coupon-remove"
                      onClick={() => setAppliedCoupon(null)}
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-espresso/50 transition-colors hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="checkout-coupon-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Coupon code"
                      className="flex-1 border border-espresso/15 bg-transparent px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] text-espresso outline-none transition-colors placeholder:normal-case placeholder:tracking-normal placeholder:text-espresso/35 focus:border-gold"
                    />
                    <button
                      type="button"
                      data-testid="checkout-coupon-apply"
                      onClick={applyCoupon}
                      disabled={couponBusy}
                      className="shrink-0 border border-espresso/20 px-5 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:border-gold hover:bg-gold/10 active:scale-[0.98] disabled:opacity-50"
                    >
                      {couponBusy ? "…" : "Apply"}
                    </button>
                  </div>
                )}
                <div className="space-y-2" data-testid="checkout-payment-methods">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold-muted">
                    Payment method
                  </p>
                  <button
                    type="button"
                    data-testid="checkout-paymethod-online"
                    onClick={() => setPayMethod("online")}
                    className={`flex w-full items-center justify-between border px-4 py-3 text-left transition-colors duration-300 ${
                      payMethod === "online"
                        ? "border-gold bg-gold/10"
                        : "border-espresso/15 hover:border-gold/50"
                    }`}
                  >
                    <span className="text-sm text-espresso">Pay Online</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-espresso/50">
                      Full amount · Razorpay
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="checkout-paymethod-cod"
                    onClick={() => setPayMethod("cod")}
                    className={`flex w-full items-center justify-between border px-4 py-3 text-left transition-colors duration-300 ${
                      payMethod === "cod"
                        ? "border-gold bg-gold/10"
                        : "border-espresso/15 hover:border-gold/50"
                    }`}
                  >
                    <span className="text-sm text-espresso">Cash on Delivery</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-espresso/50">
                      ₹200 advance now
                    </span>
                  </button>
                </div>
                {payMethod === "cod" && (
                  <p
                    data-testid="checkout-cod-notice"
                    className="border border-gold/40 bg-gold/10 px-4 py-3 text-xs leading-relaxed text-espresso/70"
                  >
                    ₹200 non-refundable shipping advance is required to confirm your COD order.
                    Remaining amount is payable on delivery.
                  </p>
                )}
                <button
                  data-testid="checkout-pay-button"
                  type="submit"
                  disabled={paying}
                  className="w-full bg-gold py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
                >
                  {paying
                    ? "Processing…"
                    : payMethod === "cod"
                      ? `Pay ${formatINR(Math.min(200, orderTotal))} Advance · Confirm COD`
                      : `Pay ${formatINR(orderTotal)}`}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CartDrawer;
